import { useEffect, useState } from 'react'

/**
 * Keystroke-to-query delay shared by the app's server-backed search boxes, so
 * every search settles at the same pace.
 */
export const SEARCH_DEBOUNCE_MS = 300

/**
 * `value`, once it has stopped changing for `delayMs` — for a search box whose
 * term is part of a query key, so typing a word issues one request, not one
 * per keystroke. The input itself stays bound to the live value.
 *
 * An empty string applies at once: clearing the box (its ×, "Clear filters")
 * must not run the other, already-reset filters against the old term for one
 * delay. Trim before passing the value in, so a whitespace-only box counts as
 * cleared and a trailing space does not restart the timer.
 */
export function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value)
  // Adjusted during render, not in the effect, so the cleared term is in the
  // same render as the reset filters.
  if (value === '' && debounced !== '') setDebounced(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}
